/** An outline row's chrome (the tour plan §5), whatever the record kind draws in it: the
 *  tree item (roving tab stop, selection, its accessible name), the drag handle and the
 *  actions' room — both kept in view mode, so pressing E moves nothing — and the row's
 *  state on the changed tint: changed, a saved row the draft removed (struck through, with
 *  Restore among its actions, until the save), the drop target while dragging. The record
 *  kind's row renders its content as children and its actions (`OutlineRowMenu`, Restore). */
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Box, IconButton, Menu } from '@mui/material';
import { useState, type MouseEvent, type ReactNode } from 'react';

import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';

import type { OutlineRowBase } from './Outline';

/** What the outline hands each row (`RecordOutline`'s `renderRow`). */
export interface OutlineRowProps {
  /** The row's place in the outline (roving focus, virtualisation). */
  index: number;
  /** The outline's one tab stop. */
  active: boolean;
  selected: boolean;
  onSelect: (index: number) => void;
}

function useRowDnd(row: OutlineRowBase, live: boolean) {
  const id = `${row.kind}:${row.id}`;
  const drag = useDraggable({ id, data: row, disabled: !live || row.removed });
  const drop = useDroppable({ id, data: row, disabled: !live || row.removed });
  return { drag, drop };
}

export interface OutlineRowShellProps extends OutlineRowProps {
  row: OutlineRowBase;
  /** The actions' room, kept in view mode too. */
  actionsWidth: number;
  /** Edit mode and allowed: the handle and the actions show. */
  live: boolean;
  /** 1 = top level (`aria-level`, and the indent below it). */
  level: number;
  /** The row's name ("Day 3 · Galway"), for its controls' names. */
  name: string;
  /** Its accessible name: the name, its counts and its state in words. */
  label: string;
  height: number;
  changed: boolean;
  children: ReactNode;
  actions: ReactNode;
}

export function OutlineRowShell({
  index,
  row,
  active,
  selected,
  onSelect,
  live,
  level,
  name,
  label,
  height,
  changed,
  children,
  actions,
  actionsWidth,
}: OutlineRowShellProps) {
  const { drag, drop } = useRowDnd(row, live);
  const dragging = drag.isDragging;
  return (
    <Box
      ref={drop.setNodeRef}
      role="treeitem"
      aria-level={level}
      aria-selected={selected}
      aria-label={label}
      tabIndex={active ? 0 : -1}
      data-itin-row={`${row.kind}:${row.id}`}
      data-row-index={index}
      data-removed={row.removed || undefined}
      data-dragging={dragging || undefined}
      onClick={() => onSelect(index)}
      sx={[
        {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          height,
          pl: 0.25 + (level - 1) * 1.25,
          pr: 0.25,
          borderRadius: 1,
          cursor: 'pointer',
          outline: 'none',
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
        },
        selected && { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.selected' } },
        drop.isOver && !dragging && { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.light}` },
        row.removed && { bgcolor: CHANGED_TINT, textDecoration: 'line-through' },
        changed && !row.removed && !selected && { bgcolor: CHANGED_TINT },
        // The source stays in place, outlined (the overlay is what moves); no opacity on
        // readable text.
        dragging && { outline: (t) => `1px dashed ${t.palette.primary.main}`, outlineOffset: -1 },
      ]}
    >
      {/* The handle's room is kept in view mode (Zero-Re-Layout). */}
      <Box sx={{ width: 20, flex: 'none', display: 'flex', justifyContent: 'center' }}>
        {live && !row.removed && (
          <Box
            component="span"
            {...drag.listeners}
            {...drag.attributes}
            tabIndex={-1}
            role="button"
            aria-label={`Drag ${name}`}
            sx={{ display: 'inline-flex', cursor: 'grab', color: 'text.secondary' }}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <DragIndicatorIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>
      {children}
      <Box data-row-actions sx={{ display: 'flex', alignItems: 'center', flex: 'none', width: actionsWidth, justifyContent: 'flex-end' }}>
        {live ? actions : null}
      </Box>
    </Box>
  );
}

/** A row's actions menu (⋮): out of the tab order — the outline's Shift+F10 opens it. Its
 *  items mount only while it is open, so they read the draft as it stands then. */
export function OutlineRowMenu({ name, items }: { name: string; items: (close: () => void) => ReactNode }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton
        size="small"
        tabIndex={-1}
        aria-label={`Actions for ${name}`}
        aria-haspopup="menu"
        data-row-menu
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ p: 0.25 }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} onClick={(e) => e.stopPropagation()}>
        {anchor ? items(close) : null}
      </Menu>
    </>
  );
}
